// src/components/Home/Home.js
import React, { useEffect, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Avatar,
  Chip,
  alpha,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

export default function Home() {
  const { authData } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  // Redirect logged-in users
  useEffect(() => {
    if (authData?.access_token) {
      navigate('/dashboard');
    }
  }, [authData, navigate]);

  // Features list
  const features = [
    {
      title: 'Verified Reviews',
      description: 'All reviews come from real subscribers who have spent money on the creator\'s content.',
      icon: <VerifiedIcon fontSize="large" sx={{ color: theme.palette.success.main }} />,
    },
    {
      title: 'Detailed Ratings',
      description: 'See scores for content quality, authenticity, frequency, and more.',
      icon: <StarIcon fontSize="large" sx={{ color: theme.palette.warning.main }} />,
    },
    {
      title: 'Spending Transparency',
      description: 'Know how much others typically spend before you subscribe.',
      icon: <TrendingUpIcon fontSize="large" sx={{ color: theme.palette.info.main }} />,
    },
    {
      title: 'Privacy Protected',
      description: 'Your identity is always kept anonymous when you leave reviews.',
      icon: <ShieldIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
  ];

  // Testimonials
  const testimonials = [
    {
      name: 'Amy Spring',
      role: 'Content Creator',
      avatar: '/testimonial.jpg',
      quote: 'OnlyStats helped me grow my audience by 300% in just two months. The verified reviews built trust with potential subscribers.',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        id="hero"
        sx={{ 
          position: 'relative',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/banner.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}
              >
                Find the Best OnlyFans Creators—Rated by Real Subscribers
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 400,
                  opacity: 0.9,
                  mb: 4,
                  maxWidth: '90%',
                }}
              >
                Don't waste your money guessing. OnlyStats helps you discover top-tier
                creators with verified reviews from actual paying subscribers.
              </Typography>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
                sx={{ mb: { xs: 6, md: 0 } }}
              >
                <Button 
                  component={RouterLink} 
                  to="/register" 
                  variant="contained" 
                  color="secondary"
                  size="large"
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '1rem',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px 0 rgba(0,0,0,0.25)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Sign Up Free
                </Button>
                
                <Button 
                  component={RouterLink} 
                  to="/login" 
                  variant="outlined"
                  size="large"
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderColor: 'white',
                    color: 'white',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Log In
                </Button>
              </Stack>
            </Grid>
            
            {!isSm && (
              <Grid item md={5}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Card
                    elevation={8}
                    sx={{
                      width: 320,
                      borderRadius: 4,
                      overflow: 'hidden',
                      transform: 'rotate(3deg)',
                      position: 'absolute',
                      top: '10%',
                      right: '5%',
                      zIndex: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'rotate(3deg) translateY(-10px)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image="/testimonial.jpg"
                      alt="Creator profile"
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Amy Spring
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <StarIcon sx={{ color: theme.palette.warning.main, mr: 0.5 }} />
                        <Typography variant="body2" fontWeight={600}>
                          4.9/5 • 98% Recommend
                        </Typography>
                      </Box>
                      <Chip 
                        label="Verified Reviews" 
                        size="small" 
                        color="success" 
                        icon={<VerifiedIcon />} 
                      />
                    </CardContent>
                  </Card>
                  
                  <Card
                    elevation={8}
                    sx={{
                      width: 320,
                      borderRadius: 4,
                      overflow: 'hidden',
                      transform: 'rotate(-5deg)',
                      position: 'absolute',
                      bottom: '10%',
                      left: '5%',
                      zIndex: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'rotate(-5deg) translateY(-10px)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image="/testimonial.webp"
                      alt="Creator profile"
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Sophia Rain
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <StarIcon sx={{ color: theme.palette.warning.main, mr: 0.5 }} />
                        <Typography variant="body2" fontWeight={600}>
                          4.7/5 • 95% Recommend
                        </Typography>
                      </Box>
                      <Chip 
                        label="Top Creator" 
                        size="small" 
                        color="primary" 
                        icon={<TrendingUpIcon />} 
                      />
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              How OnlyStats Works
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                mb: 4,
              }}
            >
              We help you make informed decisions about which creators to subscribe to
            </Typography>
          </Box>
          
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index} sx={{ display: 'flex' }}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: 280,
                      width: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        background: index === 0 ? theme.palette.success.main :
                                  index === 1 ? theme.palette.warning.main :
                                  index === 2 ? theme.palette.info.main :
                                  theme.palette.primary.main,
                        borderTopLeftRadius: 3,
                        borderTopRightRadius: 3,
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box>
                        <Box 
                          sx={{ 
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            bgcolor: alpha(
                              index === 0 ? theme.palette.success.main :
                              index === 1 ? theme.palette.warning.main :
                              index === 2 ? theme.palette.info.main :
                              theme.palette.primary.main,
                              0.1
                            ),
                            mx: 'auto',
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ minHeight: 32 }}>
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box 
        id="how-it-works"
        sx={{ 
          py: { xs: 8, md: 12 }, 
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="center"
            direction={isMdUp ? 'row-reverse' : 'column'}
          >
            {/* Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: 8,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/how-it-works.webp"
                  alt="How it works"
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 400 },
                    objectFit: 'cover',
                    display: 'block',
                    transform: 'scale(1.01)',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    zIndex: 2,
                    color: 'white',
                  }}
                >
                  <Chip 
                    label="Verified Reviews Only" 
                    color="secondary" 
                    icon={<VerifiedIcon />} 
                    sx={{ mb: 1 }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Text */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography 
                  variant="h3" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  Make Better Subscription Choices
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  {[
                    {
                      title: 'Browse & Filter',
                      description: 'Search & filter by category, price, and ratings to find creators that match your interests.',
                      icon: <SearchIcon color="primary" />,
                    },
                    {
                      title: 'Read Verified Reviews',
                      description: 'Only verified subscribers can post—no fake accounts or paid reviews.',
                      icon: <VerifiedIcon color="success" />,
                    },
                    {
                      title: 'Share Your Experience',
                      description: 'Rate creators you\'ve subscribed to and help others make informed decisions.',
                      icon: <StarIcon color="warning" />,
                    },
                    {
                      title: 'Discover Hidden Gems',
                      description: 'Find creators who consistently deliver high-quality content based on real subscriber feedback.',
                      icon: <VisibilityIcon color="info" />,
                    },
                  ].map((item, i) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: i % 2 === 0 ? 'transparent' : alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <Box 
                        sx={{ 
                          mr: 2, 
                          mt: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* For Creators Section */}
      <Box id="for-creators" sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="center"
            direction={isMdUp ? 'row' : 'column'}
          >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography 
                  variant="h3" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  For Creators
                </Typography>
                
                <Typography 
                  variant="h6" 
                  paragraph
                  sx={{ 
                    mb: 4,
                    color: 'text.primary',
                    maxWidth: 500,
                  }}
                >
                  Earn more trust and grow your following by inviting subscribers to share honest
                  reviews of your content.
                </Typography>
                
                <Box sx={{ mb: 4, width: '100%' }}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      borderRadius: 3,
                      p: 3,
                      bgcolor: alpha(theme.palette.secondary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight={600} color="secondary.main">
                      Creator Referral Program
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Our creator referral program is now live—get rewarded when your fans sign up
                      and review you on OnlyStats!
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label="Earn Rewards" 
                        color="secondary" 
                        size="small"
                      />
                      <Chip 
                        label="Grow Your Audience" 
                        color="secondary" 
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Card>
                </Box>
                
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Join as Creator
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: 8,
                }}
              >
                <Box
                  component="img"
                  src="/for-creators.avif"
                  alt="For Creators"
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 400 },
                    objectFit: 'cover',
                    display: 'block',
                    transform: 'scale(1.01)',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Privacy & Safety Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 }, 
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/privacy-safety.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid
            container
            spacing={6}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography 
                  variant="h3" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    mb: 3,
                    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  }}
                >
                  Privacy & Safety
                </Typography>
                
                <Typography 
                  variant="h6" 
                  paragraph
                  sx={{ 
                    mb: 4,
                    opacity: 0.9,
                    maxWidth: 500,
                  }}
                >
                  Your privacy and security are our top priorities. We've built OnlyStats with the strongest protections in place.
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  {[
                    {
                      title: 'Anonymous Reviews',
                      description: 'All reviews remain anonymous—no personal data is revealed to the public.',
                      icon: <SecurityIcon />,
                    },
                    {
                      title: 'Data Protection',
                      description: 'We never sell your information or share it with third parties.',
                      icon: <LockIcon />,
                    },
                    {
                      title: 'End-to-End Encryption',
                      description: 'Sensitive data is encrypted end-to-end, so your privacy is always protected.',
                      icon: <ShieldIcon />,
                    },
                  ].map((item, i) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('rgba(255,255,255,0.1)', 0.1),
                      }}
                    >
                      <Box 
                        sx={{ 
                          mr: 2, 
                          mt: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
            
            {!isSm && (
              <Grid item md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transform: 'perspective(1000px) rotateY(-5deg)',
                    transition: 'all 0.5s ease',
                    '&:hover': {
                      transform: 'perspective(1000px) rotateY(0deg)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/privacy-safety.jpg"
                    alt="Privacy & Safety"
                    sx={{
                      width: '100%',
                      height: 400,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              What Our Users Say
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Hear from creators and subscribers who use OnlyStats
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={8} key={index}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                    p: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      sx={{ 
                        width: 64, 
                        height: 64,
                        mr: 2,
                        border: `3px solid ${theme.palette.primary.main}`,
                      }}
                    />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontStyle: 'italic',
                      color: 'text.primary',
                      lineHeight: 1.6,
                      mb: 2,
                    }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Join OnlyStats Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
